import React, { createContext, forwardRef, memo, useContext, useMemo, useState } from 'react';

import { AgGridReact } from 'ag-grid-react';

import { cn } from 'lib/helpers/utility';

import {
  EMPTY_STATUS_PANEL_GROUPS,
  canSlotStatusPanels,
  createSlotStatusBar,
  groupStatusPanels,
  hasStatusPanels
} from '../lib/statusBarSlots';

const StatusPanelsContext = createContext(EMPTY_STATUS_PANEL_GROUPS);

// AG Grid waits for a React status panel to put a node in its container, so an empty slot still
// renders one.
const StatusBarSlot = memo(({ slot, ...gridParams }) => {
  const panels = useContext(StatusPanelsContext)[slot];

  if (!panels?.length) return <div className="hidden" />;

  const rendered = panels.map(({ key, statusPanel: Panel, statusPanelParams }, index) => (
    <Panel key={key ?? index} {...gridParams} {...statusPanelParams} />
  ));

  return rendered.length === 1 ? (
    rendered[0]
  ) : (
    <div className="flex h-full items-center">{rendered}</div>
  );
});

// AG Grid rebuilds every status panel whenever the `statusBar` option changes identity, and a
// rebuild that lands while React panels are still mounting appends the stale panels next to the
// new ones (duplicate pagination). The grid therefore gets one fixed left/center/right layout for
// its whole life, and panel params reach the mounted panels through context.
export const useStableStatusBar = (statusBar) => {
  const canSlot = canSlotStatusPanels(statusBar);
  const isEmpty = !hasStatusPanels(statusBar);
  const [hasSlots, setHasSlots] = useState(false);

  if (canSlot && !hasSlots) setHasSlots(true);

  const slotStatusBar = useMemo(() => createSlotStatusBar(StatusBarSlot), []);
  const panels = useMemo(() => groupStatusPanels(statusBar), [statusBar]);
  const isSlotted = canSlot || (hasSlots && isEmpty);

  return {
    gridStatusBar: isSlotted ? slotStatusBar : statusBar,
    panels,
    isStatusBarHidden: isSlotted && isEmpty
  };
};

const StableStatusBarGrid = forwardRef(({ statusBar, className, ...props }, ref) => {
  const { gridStatusBar, panels, isStatusBarHidden } = useStableStatusBar(statusBar);

  return (
    <StatusPanelsContext.Provider value={panels}>
      <AgGridReact
        ref={ref}
        className={cn(className, isStatusBarHidden && 'ag-status-bar-hidden') || undefined}
        statusBar={gridStatusBar}
        {...props}
      />
    </StatusPanelsContext.Provider>
  );
});

export default StableStatusBarGrid;
